import { FirehoseClient, PutRecordBatchCommand } from "@aws-sdk/client-firehose";
import {
  AnalyticsEvent,
  RetryEvent,
  UserActivityTrackCredentials,
} from "@plugins/user-activity-track/types.ts";
import { AWS_CONFIG } from "@plugins/user-activity-track/service/user.activity.track.constant.ts";
import { FirehoseClientConfig } from "@aws-sdk/client-firehose/dist-types/FirehoseClient";

export class AWSFirehoseProvider {
  private client!: FirehoseClient;

  private readonly MAX_RETRIES = 400;
  private readonly RETRY_DELAY = 1000; // 1 second

  constructor(credentials: UserActivityTrackCredentials) {
    this.initFirehoseClientAndCredentials(credentials);
  }

  async send(eventQueue: AnalyticsEvent[]): Promise<AnalyticsEvent[]> {
    if (eventQueue.length === 0) return eventQueue;

    const events = [...eventQueue] as RetryEvent[];

    const retryEvents = [] as RetryEvent[];

    const command = new PutRecordBatchCommand({
      DeliveryStreamName: AWS_CONFIG.FIREHOSE.STREAM,
      Records: events.map((event) => ({
        Data: new TextEncoder().encode(JSON.stringify(event)),
      })),
    });

    try {
      const response = await this.client.send(command);

      if (response.FailedPutCount && response.FailedPutCount > 0) {
        const failedRecords = events
          .filter((_, index) => response.RequestResponses?.[index]?.ErrorCode)
          .map((event) => ({
            ...event,
            retryCount: (event.retryCount || 0) + 1,
          }));

        // Separate records that can be retried from those that exceeded max retries
        const [retryable, permanent] = failedRecords.reduce<[RetryEvent[], RetryEvent[]]>(
          ([retry, perm], record) => {
            if (record.retryCount! <= this.MAX_RETRIES) {
              retry.push(record);
            } else {
              perm.push(record);
            }
            return [retry, perm];
          },
          [[], []],
        );

        if (permanent.length > 0) {
          console.error(
            `${permanent.length} records failed permanently after ${this.MAX_RETRIES} retries`,
          );
        }

        if (retryable.length > 0) {
          console.log(`Have failed records, resent events`);
          await new Promise((resolve) => setTimeout(resolve, this.RETRY_DELAY));
          retryEvents.push(...retryable);
        }
      }
    } catch (error) {
      console.error("Firehose batch error:", error);
      // Only retry events that haven't exceeded max retries
      const retryableEvents = events.filter((event) => (event.retryCount || 0) < this.MAX_RETRIES);
      if (retryableEvents.length > 0) {
        retryEvents.push(...retryableEvents);
      }
    }
    return retryEvents;
  }

  initFirehoseClientAndCredentials(credentials: UserActivityTrackCredentials) {
    this.client = new FirehoseClient({
      region: AWS_CONFIG.AWS_REGION,
      credentials: {
        accessKeyId: credentials?.accessKeyId,
        secretAccessKey: credentials?.accessKey,
        sessionToken: credentials?.sessionToken,
      },
    } as FirehoseClientConfig);
  }
}
